/**
 * Data Deletion Logs Service
 * Handles audit log retrieval and management for deleted entities
 * Requires admin authorization
 */

import api from './apiClient';

/**
 * @typedef {Object} DataDeletionLog
 * @property {number} id - Log entry ID
 * @property {string} entityType - Type of deleted entity
 * @property {number} entityId - Original entity ID
 * @property {string} entityDescription - Human-readable description
 * @property {Object} [entitySnapshot] - Snapshot of deleted data
 * @property {string} [deletedByUserId] - User ID who deleted
 * @property {string} [deletedByUserEmail] - Email of user who deleted
 * @property {string} [deletedByUserDisplayName] - Display name of user who deleted
 * @property {string} deletedAt - Deletion timestamp
 * @property {string} [deletionReason] - Reason for deletion
 * @property {string} [correlationId] - Request correlation ID
 * @property {string} [clientIpAddress] - Client IP address
 * @property {Object} [metadata] - Additional metadata
 */

/**
 * @typedef {Object} DataDeletionLogSummary
 * @property {number} id - Log entry ID
 * @property {string} entityType - Type of deleted entity
 * @property {number} entityId - Original entity ID
 * @property {string} entityDescription - Human-readable description
 * @property {string} [deletedByUserEmail] - Email of user who deleted
 * @property {string} [deletedByUserDisplayName] - Display name of user who deleted
 * @property {string} deletedAt - Deletion timestamp
 * @property {string} [deletionReason] - Reason for deletion
 */

/**
 * @typedef {Object} DataDeletionLogPagedResult
 * @property {DataDeletionLogSummary[]} items - Array of log entries
 * @property {number} totalCount - Total number of entries
 * @property {number} page - Current page number
 * @property {number} pageSize - Items per page
 * @property {number} totalPages - Total number of pages
 * @property {boolean} hasNextPage - Whether more pages exist
 * @property {boolean} hasPreviousPage - Whether previous pages exist
 */

/**
 * @typedef {Object} DataDeletionLogStats
 * @property {number} totalLogs - Total log entries
 * @property {number} logsLast24Hours - Logs in last 24 hours
 * @property {number} logsLast7Days - Logs in last 7 days
 * @property {number} logsLast30Days - Logs in last 30 days
 * @property {Object.<string, number>} byEntityType - Count by entity type
 * @property {string} [oldestLog] - Oldest log timestamp
 * @property {string} [newestLog] - Newest log timestamp
 */

/**
 * @typedef {Object} DataDeletionLogQueryParams
 * @property {number} [page=1] - Page number
 * @property {number} [pageSize=50] - Items per page
 * @property {string} [entityType] - Filter by entity type
 * @property {string} [search] - Search in description, email, or display name
 * @property {string} [deletedByUserId] - Filter by user who deleted
 * @property {string} [fromDate] - Filter deletions after this date (ISO string)
 * @property {string} [toDate] - Filter deletions before this date (ISO string)
 * @property {string} [sortBy='DeletedAt'] - Sort by field
 * @property {boolean} [sortDescending=true] - Sort descending
 */

/**
 * Get paginated list of deletion logs
 * @param {DataDeletionLogQueryParams} [params={}] - Query parameters
 * @param {Object} [options] - Request options
 * @returns {Promise<DataDeletionLogPagedResult>}
 */
export const getLogs = async (params = {}, options = {}) => {
  return api.get('/datadeletionlogs', params, options);
};

/**
 * Get a single deletion log by ID with full details
 * @param {number} id - Log entry ID
 * @param {Object} [options] - Request options
 * @returns {Promise<DataDeletionLog>}
 */
export const getLog = async (id, options = {}) => {
  return api.get(`/datadeletionlogs/${id}`, {}, options);
};

/**
 * Get deletion log statistics
 * @param {Object} [options] - Request options
 * @returns {Promise<DataDeletionLogStats>}
 */
export const getStats = async (options = {}) => {
  return api.get('/datadeletionlogs/stats', {}, options);
};

/**
 * Get list of entity types that have deletion logs
 * @param {Object} [options] - Request options
 * @returns {Promise<string[]>}
 */
export const getEntityTypes = async (options = {}) => {
  return api.get('/datadeletionlogs/entity-types', {}, options);
};

/**
 * Delete a single deletion log entry
 * @param {number} id - Log entry ID
 * @param {Object} [options] - Request options
 * @returns {Promise<void>}
 */
export const deleteLog = async (id, options = {}) => {
  return api.delete(`/datadeletionlogs/${id}`, {
    ...options,
    successMessage: 'Deletion log entry removed',
  });
};

/**
 * Cleanup old deletion logs
 * @param {Object} params - Cleanup parameters
 * @param {string} params.olderThan - Delete logs older than this date (ISO string)
 * @param {string} [params.entityType] - Optional entity type filter
 * @param {Object} [options] - Request options
 * @returns {Promise<{deletedCount: number, message: string}>}
 */
export const cleanupLogs = async (params, options = {}) => {
  return api.post('/datadeletionlogs/cleanup', params, {
    ...options,
    successMessage: `Cleaned up ${params.deletedCount || ''} old logs`.trim(),
  });
};

/**
 * Export deletion logs for a date range
 * @param {Object} params - Export parameters
 * @param {string} params.fromDate - Start date (ISO string)
 * @param {string} params.toDate - End date (ISO string)
 * @param {string} [params.entityType] - Optional entity type filter
 * @param {Object} [options] - Request options
 * @returns {Promise<DataDeletionLog[]>}
 */
export const exportLogs = async (params, options = {}) => {
  return api.get('/datadeletionlogs/export', params, options);
};

/**
 * Format timestamp for display
 * @param {string} timestamp - ISO timestamp
 * @returns {string} Formatted timestamp
 */
export const formatTimestamp = (timestamp) => {
  if (!timestamp) return '';
  const date = new Date(timestamp);
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
};

/**
 * Get relative time string (e.g., "5 minutes ago")
 * @param {string} timestamp - ISO timestamp
 * @returns {string} Relative time string
 */
export const getRelativeTime = (timestamp) => {
  if (!timestamp) return '';
  
  const now = new Date();
  const date = new Date(timestamp);
  const diffMs = now - date;
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);

  if (diffSec < 60) return 'just now';
  if (diffMin < 60) return `${diffMin} minute${diffMin !== 1 ? 's' : ''} ago`;
  if (diffHour < 24) return `${diffHour} hour${diffHour !== 1 ? 's' : ''} ago`;
  if (diffDay < 7) return `${diffDay} day${diffDay !== 1 ? 's' : ''} ago`;
  
  return formatTimestamp(timestamp);
};

/**
 * Entity type display colors
 */
export const EntityTypeColors = {
  FileEntity: '#1890ff',
  Country: '#52c41a',
  LevelOfDifficulty: '#faad14',
  ApplicationUser: '#722ed1',
  Course: '#eb2f96',
  Lesson: '#13c2c2',
  default: '#8c8c8c',
};

/**
 * Get color for entity type
 * @param {string} entityType - Entity type name
 * @returns {string} Hex color
 */
export const getEntityTypeColor = (entityType) => {
  return EntityTypeColors[entityType] || EntityTypeColors.default;
};

const dataDeletionLogsService = {
  getLogs,
  getLog,
  getStats,
  getEntityTypes,
  deleteLog,
  cleanupLogs,
  exportLogs,
  formatTimestamp,
  getRelativeTime,
  EntityTypeColors,
  getEntityTypeColor,
};

export default dataDeletionLogsService;
